import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { isDefined } from 'remeda';
import { NotificationTypesSchema } from 'types/notification-types';
import { z } from 'zod/v4';

import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  useLegacyAxiosInstance,
} from './utils';

const ContactPersonAddressSchema = z
  .object({
    id: z.string(),
    attributes: z.object({
      street_name: z.string().nullable(),
      street_number: z.string().nullable(),
      street_box: z.string().nullable(),
      zip_code: z.string().nullable(),
      city: z.string().nullable(),
      country_code: z.string().nullable(),
      additional_line_1: z.string().nullable(),
      additional_line_2: z.string().nullable(),
      country: z.string().nullable(),
    }),
  })
  .transform(item => ({
    id: item.id,
    ...item.attributes,
  }));

const PhoneSchema = z
  .object({
    id: z.string(),
    attributes: z.object({
      number: z.string(),
    }),
  })
  .transform(item => ({
    id: item.id,
    ...item.attributes,
  }));

export const ContactPersonSchema = z
  .object({
    id: z.string(),
    attributes: z.object({
      address: ContactPersonAddressSchema.nullable(),
      emails: z.array(z.email()),
      first_name: z.string(),
      job_title: z.string().nullable(),
      last_name: z.string(),
      // Not narrowed to the known set: a contact person in a language this build does not
      // list must still load, rather than throwing the whole parse.
      locale: z.string().nullable(),
      main_contact: z.boolean(),
      notifications: z.array(NotificationTypesSchema),
      phones: z.array(PhoneSchema),
      use_postal_address: z.boolean().optional(),
    }),
  })
  .transform(item => ({
    id: item.id,
    ...item.attributes,
  }));

export type ContactPerson = z.infer<typeof ContactPersonSchema>;

export async function loadContactPersonQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(
    `/debtors/${queryKey[1].debtorId}/contact_persons/${queryKey[1].contactPersonId}`,
  );

  const parseResult = ContactPersonSchema.safeParse(data.data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'contactPerson');
    throw parseResult.error;
  }

  return parseResult.data;
}

type LoadContactPersonVariables = {
  debtorId: string;
  contactPersonId?: string;
  isEditMode?: boolean;
};

const emptyContactPerson: ContactPerson = {
  id: '',
  first_name: '',
  last_name: '',
  job_title: null,
  locale: null,
  main_contact: false,
  emails: [],
  phones: [],
  notifications: [],
  address: null,
  use_postal_address: false,
};

export function useLoadContactPerson(variables: LoadContactPersonVariables) {
  const axiosInstance = useLegacyAxiosInstance();

  const queryResult = useQuery<ContactPerson, ApiError>({
    queryKey: ['contact-person', variables],
    queryFn: context => loadContactPersonQueryFn(axiosInstance, context),
    enabled: variables.isEditMode,
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return emptyContactPerson;
    },
  });

  return addResourceNameToQueryResult<ContactPerson, ApiError, 'contactPerson', 'PreventUndefined'>(
    'contactPerson',
    queryResult,
    emptyContactPerson,
  );
}
